// /bills/:id — Bill Detail (admin)
//
// Read-mostly screen owners and managers open from RecentBills, audit
// logs, or search. Wraps the existing /api/v1/bills/:id endpoint and
// offers the small set of mutating actions the backend already supports:
//
//   - Print receipt / 80mm / 58mm    (any status)
//   - Download invoice PDF           (any status)
//   - Download credit-note PDF       (RETURNED only)
//   - Void                           (DRAFT only — backend rejects others)
//   - Initiate return                (COMPLETED only)
//
// Role gating mirrors the API: MANAGER+ for void/return. Buttons that the
// current user can't use simply don't render.

import { createContext, useContext, useMemo, useState } from 'react';
import { formatBillNumber } from '../../features/bills/bill-number';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  PageContainer,
  Button,
  Skeleton,
  Modal,
  Select,
  TextArea,
  Alert,
  ErrorBoundary,
  Timeline,
  useToast,
} from '@bbpos/ui';
import type { TimelineItem, ToastVariant } from '@bbpos/ui';
import { formatPaise } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { useAuthStore } from '@/stores/use-auth-store';
import { useStoreTimezone } from '@/lib/store-clock';
import { ROLE_LEVELS } from '@/types/user';
import { ApiError } from '@/lib/api-client';
import { GLASS, cn } from '@/constants/design-tokens';
import {
  useBill,
  useVoidBill,
  useInitiateReturn,
  billsApi,
} from '@/features/bills';
import type {
  BillItem,
  BillWithItems,
  InitiateReturnInput,
  ReturnMode,
  ReturnReason,
} from '@/features/bills';
import { billStatusMeta, BillStatusPill as StatusPill } from './status-meta';

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** On the store's clock (T18), not this browser's. */
function formatDateTime(iso: string, timeZone: string): string {
  return new Date(iso).toLocaleString('en-IN', {
    timeZone,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function mapMutationError(err: unknown): { title: string; description?: string; variant: ToastVariant } {
  if (err instanceof ApiError) {
    return { title: err.message || 'Request failed', variant: 'error' };
  }
  return { title: 'Something went wrong. Please try again.', variant: 'error' };
}

const RETURN_MODE_OPTIONS: { value: ReturnMode; label: string }[] = [
  { value: 'CASH', label: 'Cash refund' },
  { value: 'STORE_CREDIT', label: 'Store credit' },
  { value: 'EXCHANGE', label: 'Exchange' },
  { value: 'PAYMENT_REVERSAL', label: 'Payment reversal' },
];

const RETURN_REASON_OPTIONS: { value: ReturnReason; label: string }[] = [
  { value: 'DEFECTIVE', label: 'Defective' },
  { value: 'CHANGED_MIND', label: 'Customer changed mind' },
  { value: 'WRONG_ITEM', label: 'Wrong item' },
  { value: 'SIZE_MISMATCH', label: 'Size mismatch' },
  { value: 'OTHER', label: 'Other' },
];

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function BillDetailSkeleton() {
  return (
    <div className="space-y-6 animate-fade-in">
      <Skeleton variant="rect" height={140} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Skeleton variant="rect" height={300} />
          <Skeleton variant="rect" height={180} />
        </div>
        <div className="space-y-6">
          <Skeleton variant="rect" height={200} />
          <Skeleton variant="rect" height={140} />
        </div>
      </div>
    </div>
  );
}

// ─── Inner content ────────────────────────────────────────────────────────────

function BillDetailContent({ bill }: { bill: BillWithItems }) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const timeZone = useStoreTimezone(bill.location_id);
  const user = useAuthStore((s) => s.user);
  const roleLevel = ROLE_LEVELS[user?.role ?? ''] ?? 0;
  const canVoid = roleLevel >= ROLE_LEVELS.MANAGER && bill.status === 'DRAFT';
  const creditNoteId = bill.return_against
    ? bill.id
    : bill.credit_notes?.[bill.credit_notes.length - 1]?.id;
  // Honour the tenant's configurable Return policy (see /settings/return-policy).
  // The profile carries a derived boolean so this page never has to repeat
  // the floor comparison locally.
  const profile = useAuthStore((s) => s.profile);
  const canReturn =
    Boolean(profile?.permissions?.can_issue_credit_note) && bill.status === 'COMPLETED';

  const status = billStatusMeta(bill.status);
  // gst-parked 090 (D5): billed while not GST registered — no tax anywhere.
  const noGst = bill.supply_type === 'NONE';

  const [showVoidModal, setShowVoidModal] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);

  const voidMutation = useVoidBill();
  const returnMutation = useInitiateReturn();

  const handleVoid = () => {
    voidMutation.mutate(bill.id, {
      onSuccess: () => {
        setShowVoidModal(false);
        toast({ title: 'Bill voided', variant: 'success' });
      },
      onError: (err) => toast(mapMutationError(err)),
    });
  };

  const timelineItems = useMemo<TimelineItem[]>(() => {
    const items: TimelineItem[] = [
      {
        id: 'created',
        type: 'success',
        action: bill.status === 'DRAFT' ? 'Draft started' : 'Bill created',
        timestamp: bill.created_at,
      },
    ];
    if (bill.status === 'PARKED' && bill.parked_at) {
      items.push({
        id: 'parked',
        type: 'parked',
        action: bill.parked_by_name ? `Bill parked by ${bill.parked_by_name}` : 'Bill parked',
        timestamp: bill.parked_at,
      });
    }
    // Each split-tender payment is a separate event.
    bill.payments.forEach((p) => {
      items.push({
        id: `payment-${p.id}`,
        type: 'success',
        action: `Payment captured — ${p.payment_method} · ${formatPaise(p.amount, currency)}`,
        timestamp: p.created_at,
      });
    });
    if (bill.status === 'VOID') {
      items.push({
        id: 'voided',
        type: 'danger',
        action: bill.void_reason ? `Bill voided — ${bill.void_reason}` : 'Bill voided',
        timestamp: bill.updated_at,
      });
    }
    if (bill.status === 'RETURNED') {
      items.push({
        id: 'returned',
        type: 'warning',
        action: 'Return processed — credit note issued',
        timestamp: bill.updated_at,
      });
    }
    return items;
  }, [bill, currency]);

  // ── Bill totals + rounding visibility ──
  const hasRounding = (bill.rounding_adjustment ?? 0) !== 0;
  const splitMethod = bill.payment_method === 'SPLIT';

  return (
    <>
      <div className="space-y-6 animate-fade-in">
        {/* ── Hero / status banner ── */}
        <section
          className={cn(
            'relative overflow-hidden rounded-2xl bg-gradient-to-br text-white p-6 shadow-lg',
            status.gradient,
          )}
        >
          <div className="pointer-events-none absolute -top-20 -right-20 h-56 w-56 rounded-full bg-white/15 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-16 -left-12 h-44 w-44 rounded-full bg-black/10 blur-3xl" />
          <div className="relative flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <StatusPill status={bill.status} />
                <span className="text-xs text-white/80">{status.description}</span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight font-mono tabular-nums text-white">
                {formatBillNumber(bill)}
              </h1>
              <p className="mt-1.5 text-sm text-white/85">
                Created {formatDateTime(bill.created_at, timeZone)}
              </p>
              <p className="text-sm text-white/85">
                Customer:{' '}
                {bill.customer_id ? (
                  <Link
                    className="font-semibold underline-offset-2 hover:underline"
                    to={`/customers/${bill.customer_id}`}
                  >
                    {bill.customer_name || 'View customer'}
                  </Link>
                ) : (
                  <span className="font-semibold">Walk-in</span>
                )}
                {bill.customer_phone ? ` · ${bill.customer_phone}` : ''}
              </p>
            </div>
            <div className="text-right shrink-0">
              <p className="text-xs uppercase tracking-wider text-white/75">Grand total</p>
              <p className="text-3xl sm:text-4xl font-bold tabular-nums">
                {formatPaise(bill.grand_total + (bill.rounding_adjustment ?? 0), currency)}
              </p>
              {bill.payment_method && (
                <p className="text-xs text-white/80 mt-1">
                  Paid via {bill.payment_method}
                </p>
              )}
            </div>
          </div>
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* ── Left column: items + payments + timeline ── */}
          <div className="lg:col-span-2 space-y-6">
            {/* Items */}
            <section className={cn(GLASS, 'overflow-hidden')}>
              <header className="flex items-center justify-between px-5 py-3 border-b border-neutral-100 dark:border-neutral-700/60">
                <h2 className="text-sm font-semibold text-neutral-800 dark:text-neutral-100">
                  Items
                  <span className="ml-2 rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-200 px-2 py-0.5 text-xs font-semibold tabular-nums">
                    {bill.items.length}
                  </span>
                </h2>
              </header>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-neutral-50 dark:bg-neutral-800/40 text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                    <tr>
                      <th className="px-5 py-2 text-left">Product</th>
                      <th className="px-3 py-2 text-left">HSN</th>
                      <th className="px-3 py-2 text-right">Qty</th>
                      <th className="px-3 py-2 text-right">Unit price</th>
                      <th className="px-3 py-2 text-right">Discount</th>
                      {!noGst && <th className="px-3 py-2 text-right">Tax</th>}
                      <th className="px-5 py-2 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bill.items.map((item) => (
                      <ItemRow key={item.id} item={item} currency={currency} showTax={!noGst} />
                    ))}
                  </tbody>
                  <TotalLabelSpan.Provider value={noGst ? 5 : 6}>
                  <tfoot className="bg-neutral-50/60 dark:bg-neutral-800/30 text-sm">
                    <TotalRow label="Subtotal" value={formatPaise(bill.subtotal, currency)} />
                    {bill.discount_total > 0 && (
                      <TotalRow
                        label="Discount"
                        value={`− ${formatPaise(bill.discount_total, currency)}`}
                        tone="danger"
                      />
                    )}
                    {noGst ? (
                      <TotalRow label="Not GST registered" value="No GST charged" sub />
                    ) : (
                      <TotalRow label="Tax" value={formatPaise(bill.tax_total, currency)} />
                    )}
                    {(bill.tax_lines ?? []).map((t) => (
                      <TotalRow
                        key={`${t.kind}-${t.ratePct}`}
                        label={`${t.kind} @${t.ratePct}%`}
                        value={formatPaise(t.amount, currency)}
                        sub
                      />
                    ))}
                    {hasRounding && (
                      <TotalRow
                        label="Round-off"
                        value={`${(bill.rounding_adjustment ?? 0) > 0 ? '+ ' : '− '}${formatPaise(
                          Math.abs(bill.rounding_adjustment ?? 0),
                          currency,
                        )}`}
                      />
                    )}
                    {/* The total is what the bill settled for: grand_total + rounding. */}
                    <TotalRow
                      label="Grand total"
                      value={formatPaise(bill.grand_total + (bill.rounding_adjustment ?? 0), currency)}
                      emphasis
                    />
                  </tfoot>
                  </TotalLabelSpan.Provider>
                </table>
              </div>
            </section>

            {/* Payments */}
            <section className={cn(GLASS, 'p-5')}>
              <div className="flex items-baseline justify-between mb-3">
                <h2 className="text-sm font-semibold text-neutral-800 dark:text-neutral-100">
                  Payments
                  <span className="ml-2 rounded-full bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-200 px-2 py-0.5 text-xs font-semibold tabular-nums">
                    {bill.payments.length || (bill.payment_method ? 1 : 0)}
                  </span>
                </h2>
                {splitMethod && (
                  <span className="text-[11px] uppercase font-semibold text-violet-600 dark:text-violet-300">
                    Split tender
                  </span>
                )}
              </div>

              {bill.payments.length > 0 ? (
                <ul className="divide-y divide-neutral-100 dark:divide-neutral-700/60">
                  {bill.payments.map((p) => (
                    <li key={p.id} className="flex items-center justify-between py-2.5">
                      <div>
                        <p className="font-medium text-neutral-800 dark:text-neutral-100">
                          {p.payment_method}
                        </p>
                        <p className="text-[11px] text-neutral-500 dark:text-neutral-400 tabular-nums">
                          {formatDateTime(p.created_at, timeZone)}
                        </p>
                      </div>
                      <p className="font-mono tabular-nums text-sm text-neutral-900 dark:text-white">
                        {formatPaise(p.amount, currency)}
                      </p>
                    </li>
                  ))}
                </ul>
              ) : bill.payment_method ? (
                <div className="flex items-center justify-between py-2">
                  <p className="font-medium text-neutral-800 dark:text-neutral-100">
                    {bill.payment_method}
                  </p>
                  <p className="font-mono tabular-nums text-sm text-neutral-900 dark:text-white">
                    {formatPaise(bill.amount_paid, currency)}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  No payments captured yet.
                </p>
              )}

              {/* Reconciliation hint — amount_paid includes the change handed back */}
              {bill.payments.length > 0 &&
                Math.abs(
                  bill.payments.reduce((s, p) => s + p.amount, 0) -
                    (bill.amount_paid - (bill.change_due ?? 0)),
                ) > 1 && (
                  <p className="mt-3 text-xs text-warning-700 dark:text-warning-300">
                    ⚠ Sum of tenders doesn&apos;t match captured amount. Reconciliation needed.
                  </p>
                )}
            </section>

            {/* Timeline */}
            <section className={cn(GLASS, 'p-5')}>
              <h2 className="text-sm font-semibold text-neutral-800 dark:text-neutral-100 mb-3">
                Activity
              </h2>
              <Timeline items={timelineItems} />
            </section>

            {/* Credit-note pointer for returned bills. T04: on a return bill
                the credit note is the bill itself; on a fully returned
                original it is the latest credit note issued against it. */}
            {bill.status === 'RETURNED' && creditNoteId && (
              <Alert
                variant="warning"
                title="Credit note available"
                description={
                  bill.redemption_code
                    ? `Store credit code: ${bill.redemption_code}`
                    : 'A credit note PDF has been issued against this bill.'
                }
                action={{
                  label: 'Download credit note',
                  onClick: () => window.open(billsApi.creditNotePdfUrl(creditNoteId), '_blank'),
                }}
              />
            )}
          </div>

          {/* ── Right rail: actions ── */}
          <aside className="space-y-6">
            <section className={cn(GLASS, 'p-5')}>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400 mb-3">
                Actions
              </h3>
              <div className="space-y-2">
                <Button
                  variant="primary"
                  className="w-full"
                  onClick={() => window.open(billsApi.invoicePdfUrl(bill.id), '_blank')}
                >
                  Download invoice
                </Button>
                <Button
                  variant="secondary"
                  className="w-full"
                  onClick={() => window.open(billsApi.receiptPdfUrl(bill.id), '_blank')}
                >
                  Print receipt (80mm)
                </Button>
                <Button
                  variant="secondary"
                  className="w-full"
                  onClick={() => window.open(billsApi.receipt58mmPdfUrl(bill.id), '_blank')}
                >
                  Print receipt (58mm)
                </Button>

                {canReturn && (
                  <Button
                    variant="secondary"
                    className="w-full"
                    onClick={() => setShowReturnModal(true)}
                    disabled={returnMutation.isPending}
                  >
                    Initiate return
                  </Button>
                )}
                {canVoid && (
                  <Button
                    variant="danger"
                    className="w-full"
                    onClick={() => setShowVoidModal(true)}
                    disabled={voidMutation.isPending}
                  >
                    Void draft
                  </Button>
                )}
              </div>
            </section>

            {/* Quick facts panel */}
            <section className={cn(GLASS, 'p-5 space-y-3 text-sm')}>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                Bill info
              </h3>
              <Fact label="Status" value={<StatusPill status={bill.status} />} />
              <Fact label="Currency" value={bill.currency} mono />
              <Fact label="Created" value={formatDateTime(bill.created_at, timeZone)} mono />
              {bill.created_at !== bill.updated_at && (
                <Fact label="Updated" value={formatDateTime(bill.updated_at, timeZone)} mono />
              )}
              {bill.status === 'PARKED' && (
                <>
                  <Fact label="Parked reference" value={bill.park_reference ?? '—'} mono />
                  <Fact label="Parked by" value={bill.parked_by_name ?? '—'} />
                  {bill.parked_at && (
                    <Fact label="Parked at" value={formatDateTime(bill.parked_at, timeZone)} mono />
                  )}
                </>
              )}
              {bill.return_against && (
                <Fact
                  label="Credit note for"
                  value={
                    <Link
                      to={`/bills/${bill.return_against}`}
                      className="text-brand-600 dark:text-brand-300 underline-offset-2 hover:underline font-mono tabular-nums"
                    >
                      View original
                    </Link>
                  }
                />
              )}
              {bill.notes && <Fact label="Notes" value={bill.notes} />}
            </section>
          </aside>
        </div>
      </div>

      {/* ── Void confirmation ── */}
      <Modal
        open={showVoidModal}
        onClose={() => setShowVoidModal(false)}
        title="Void this draft bill?"
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => setShowVoidModal(false)}
              disabled={voidMutation.isPending}
            >
              Keep bill
            </Button>
            <Button
              variant="danger"
              onClick={handleVoid}
              disabled={voidMutation.isPending}
            >
              {voidMutation.isPending ? 'Voiding…' : 'Void draft'}
            </Button>
          </div>
        }
      >
        <p className="text-sm text-neutral-700 dark:text-neutral-200">
          {formatBillNumber(bill)} will be moved to <strong>Void</strong>. The draft
          items will be discarded. This cannot be undone.
        </p>
      </Modal>

      {/* ── Initiate return ── */}
      <InitiateReturnModal
        open={showReturnModal}
        bill={bill}
        currency={currency}
        onClose={() => setShowReturnModal(false)}
        mutation={returnMutation}
        onSuccess={(creditNoteId) => {
          setShowReturnModal(false);
          toast({
            title: 'Return processed',
            description: 'A credit note has been created.',
            variant: 'success',
          });
          // Navigate to the credit note so staff see the result. Falls back
          // to refetching the current bill, which now reads as RETURNED.
          if (creditNoteId) navigate(`/bills/${creditNoteId}`);
        }}
      />
    </>
  );
}

// ─── Item row ─────────────────────────────────────────────────────────────────

function ItemRow({ item, currency, showTax = true }: { item: BillItem; currency: string; showTax?: boolean }) {
  return (
    <tr className="border-t border-neutral-100 dark:border-neutral-700/60 hover:bg-neutral-50/60 dark:hover:bg-neutral-800/40">
      <td className="px-5 py-2.5">
        <p className="font-medium text-neutral-900 dark:text-white truncate">
          {item.product_name}
        </p>
        <p className="text-[11px] text-neutral-500 dark:text-neutral-400 font-mono">
          {item.product_sku}
        </p>
      </td>
      <td className="px-3 py-2.5 text-neutral-600 dark:text-neutral-300 font-mono text-xs">
        {item.hsn_code || '—'}
      </td>
      <td className="px-3 py-2.5 text-right tabular-nums">
        {Number(item.quantity).toLocaleString('en-IN')}
        {item.unit ? <span className="text-[11px] text-neutral-400 ml-0.5">{item.unit}</span> : null}
      </td>
      <td className="px-3 py-2.5 text-right tabular-nums">
        {formatPaise(item.unit_price, currency as 'INR')}
      </td>
      <td className="px-3 py-2.5 text-right tabular-nums text-danger-600 dark:text-danger-400">
        {item.line_discount > 0 ? `− ${formatPaise(item.line_discount, currency as 'INR')}` : '—'}
      </td>
      {showTax && (
        <td className="px-3 py-2.5 text-right tabular-nums text-neutral-600 dark:text-neutral-300">
          {formatPaise(item.line_tax, currency as 'INR')}
          <span className="text-[11px] text-neutral-400 ml-1">({item.gst_rate}%)</span>
        </td>
      )}
      <td className="px-5 py-2.5 text-right font-semibold tabular-nums text-neutral-900 dark:text-white">
        {formatPaise(item.line_total, currency as 'INR')}
      </td>
    </tr>
  );
}

/** Columns the totals label spans — one fewer when the Tax column is hidden (gst-parked 090). */
const TotalLabelSpan = createContext(6);

function TotalRow({
  label,
  value,
  emphasis,
  tone,
  sub,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
  tone?: 'danger';
  /** A breakdown row under the one above (e.g. CGST @9% under Tax). */
  sub?: boolean;
}) {
  const labelSpan = useContext(TotalLabelSpan);
  return (
    <tr>
      <td
        colSpan={labelSpan}
        className={cn(
          'px-5 text-right text-neutral-500 dark:text-neutral-400',
          sub ? 'py-0.5 text-xs' : 'py-1.5 text-sm',
        )}
      >
        {label}
      </td>
      <td
        className={cn(
          'px-5 text-right tabular-nums font-mono',
          sub ? 'py-0.5' : 'py-1.5',
          emphasis
            ? 'text-base font-bold text-neutral-900 dark:text-white'
            : sub
              ? 'text-xs text-neutral-600 dark:text-neutral-300'
              : 'text-sm text-neutral-800 dark:text-neutral-100',
          tone === 'danger' ? 'text-danger-600 dark:text-danger-400' : '',
        )}
      >
        {value}
      </td>
    </tr>
  );
}

function Fact({
  label,
  value,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-xs text-neutral-500 dark:text-neutral-400">{label}</span>
      <span
        className={cn(
          'text-sm text-neutral-800 dark:text-neutral-100 text-right',
          mono ? 'font-mono tabular-nums' : '',
        )}
      >
        {value}
      </span>
    </div>
  );
}

// ─── Initiate return modal ────────────────────────────────────────────────────

interface InitiateReturnMutation {
  isPending: boolean;
  mutate: (
    vars: { id: string; input: InitiateReturnInput },
    opts: {
      onSuccess?: (data: { credit_note: { id: string } }) => void;
      onError?: (err: unknown) => void;
    },
  ) => void;
}

function InitiateReturnModal({
  open,
  bill,
  currency,
  onClose,
  mutation,
  onSuccess,
}: {
  open: boolean;
  bill: BillWithItems;
  currency: string;
  onClose: () => void;
  mutation: InitiateReturnMutation;
  onSuccess: (creditNoteId: string | null) => void;
}) {
  const { toast } = useToast();
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [returnMode, setReturnMode] = useState<ReturnMode>('CASH');
  const [reason, setReason] = useState<ReturnReason>('DEFECTIVE');
  const [reasonNotes, setReasonNotes] = useState('');

  // Reset state when reopened against a new bill.
  // (Effect-free reset is fine here — values are local UI state only.)
  if (!open && Object.keys(selected).length > 0) {
    setTimeout(() => setSelected({}), 0);
  }

  const selectedIds = Object.entries(selected)
    .filter(([, v]) => v)
    .map(([k]) => k);

  const refundAmount = bill.items
    .filter((it) => selected[it.id])
    .reduce((s, it) => s + it.line_total, 0);

  const handleSubmit = () => {
    if (selectedIds.length === 0) {
      toast({ title: 'Select at least one item to return', variant: 'warning' });
      return;
    }
    mutation.mutate(
      {
        id: bill.id,
        input: {
          item_ids: selectedIds,
          return_mode: returnMode,
          reason,
          reason_notes: reasonNotes.trim() || undefined,
        },
      },
      {
        onSuccess: (data) => onSuccess(data?.credit_note?.id ?? null),
        onError: (err) => toast(mapMutationError(err)),
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Initiate return"
      footer={
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm text-neutral-600 dark:text-neutral-300">
            Refund:{' '}
            <strong className="text-neutral-900 dark:text-white tabular-nums">
              {formatPaise(refundAmount, currency as 'INR')}
            </strong>
          </span>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSubmit} disabled={mutation.isPending || selectedIds.length === 0}>
              {mutation.isPending ? 'Processing…' : 'Process return'}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">
            Items to return
          </label>
          <ul className="rounded-lg border border-neutral-200 dark:border-neutral-700 divide-y divide-neutral-100 dark:divide-neutral-700/60 max-h-64 overflow-y-auto">
            {bill.items.map((it) => (
              <li
                key={it.id}
                className="flex items-center gap-3 px-3 py-2 hover:bg-neutral-50/80 dark:hover:bg-neutral-800/40"
              >
                <input
                  type="checkbox"
                  checked={!!selected[it.id]}
                  onChange={(e) => setSelected((s) => ({ ...s, [it.id]: e.target.checked }))}
                  className="h-4 w-4 rounded border-neutral-300 text-brand-600 focus:ring-brand-500"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-neutral-900 dark:text-white truncate">
                    {it.product_name}
                  </p>
                  <p className="text-[11px] text-neutral-500 dark:text-neutral-400 font-mono">
                    {it.product_sku} · qty {Number(it.quantity).toLocaleString('en-IN')}
                  </p>
                </div>
                <p className="text-sm font-mono tabular-nums text-neutral-700 dark:text-neutral-200">
                  {formatPaise(it.line_total, currency as 'INR')}
                </p>
              </li>
            ))}
          </ul>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Refund mode
            </label>
            <Select
              options={RETURN_MODE_OPTIONS}
              value={returnMode}
              onChange={(v) => setReturnMode(v as ReturnMode)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Reason
            </label>
            <Select
              options={RETURN_REASON_OPTIONS}
              value={reason}
              onChange={(v) => setReason(v as ReturnReason)}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
            Notes (optional)
          </label>
          <TextArea
            value={reasonNotes}
            onChange={(v) => setReasonNotes(v)}
            placeholder="Additional context for the audit log…"
            rows={3}
          />
        </div>
      </div>
    </Modal>
  );
}

// ─── Page wrapper ─────────────────────────────────────────────────────────────

export function BillDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: bill, isLoading, error } = useBill(id);

  return (
    <PageContainer
      title=""
      breadcrumbs={[
        { label: 'Home', href: '/home' },
        { label: 'Bills', href: '/bills' },
        { label: bill ? formatBillNumber(bill) : (id ?? 'Bill') },
      ]}
    >
      <ErrorBoundary resetKey={`bill-${id}`}>
        {isLoading ? (
          <BillDetailSkeleton />
        ) : error ? (
          <Alert
            variant="danger"
            title="Failed to load bill"
            description={error instanceof Error ? error.message : 'Something went wrong.'}
            action={{ label: 'Back', onClick: () => navigate(-1) }}
          />
        ) : !bill ? (
          <Alert
            variant="warning"
            title="Bill not found"
            description="This bill may have been deleted or you do not have access to it."
            action={{ label: 'Back', onClick: () => navigate(-1) }}
          />
        ) : (
          <BillDetailContent bill={bill} />
        )}
      </ErrorBoundary>
    </PageContainer>
  );
}
